import { useState, useRef, useEffect } from "react";
import Image from "../../../../components/images/Image";
import { forwardRef, useImperativeHandle } from "react";
import useApi from "../../../../services/useApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { handleUpload } from "../../../../components/S3uploads/S3Function";
import { setAddLoading, setPhotoUpload } from "../addMDSlice";
import ErrorMessage from "../../../../components/ErrorMessages/ErrorMessage";
import { setSavedData } from "../addMDSlice";
import { useDispatch } from "react-redux";
import config from "../../../../config/config";
import { useNavigate } from "react-router";
import { showToast } from "../../../../components/toastMessage/toastSlice";
import { clearSignaturePath } from "../../../../store/slices/signatureSlice";
import CustomersTermsAndConditions from "../../../../components/CustomModal/CustomersTermsAndConditions";

interface HTMLVideoElementWithSrcObject extends HTMLVideoElement {
    srcObject: MediaStream | null;
}
export interface PhotoUploadRef {
    handleSubmit: () => void;
}

interface PhotoUploadWithValidationProps {
    handleNext: () => void;
    setUploadedImage: React.Dispatch<React.SetStateAction<string | null>>;
    uploadedImage: string | null;
    selectedOption: string;
    type: string;
    isUploadedPhoto?: boolean;
    isFemale?: boolean;
    id?: number | undefined;
    setTermsAndConditions?: React.Dispatch<React.SetStateAction<boolean>>;
    termsAndConditions?: boolean;
}

const PhotoUploadWithValidation = forwardRef<PhotoUploadRef, PhotoUploadWithValidationProps>(({ handleNext, selectedOption, uploadedImage, isFemale, setUploadedImage, isUploadedPhoto, type, id, setTermsAndConditions, termsAndConditions }, ref) => {
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [image, setImage] = useState<string | null>(null);

    const navigate = useNavigate();
    const [showCamera, setShowCamera] = useState(false);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [captureError, setCaptureError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCaptured, setIsCaptured] = useState(false);
    const { PatchAPI } = useApi();
    const videoRef = useRef<HTMLVideoElementWithSrcObject | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const imageRef = useRef<HTMLInputElement | null>(null);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const { user_id, savedData, profile } = useSelector((state: RootState) => state.addMD);
    const [acceptButtonLoading, setAcceptButtonLoading] = useState(false);
    const getUrlPath = (type: any) => type?.toLowerCase().split(' ').join('-');

    const dispatch = useDispatch();
    useImperativeHandle(ref, () => ({
        handleSubmit: () => {
            // Logic to handle the submission of the photo
            handleFileSubmit();
        },
    }));
    // Handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                // Close the modal and reset the captured photo
                setIsModalOpen(false);
                setCapturedImage(null);
                setIsCaptured(false);
                stopCamera();
            }
        };

        if (isModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isModalOpen]);

    // Start/stop camera when modal is opened/closed
    useEffect(() => {
        if (id || user_id) {
            const savedPhoto = savedData?.['photo'] || "";
            dispatch(setPhotoUpload(savedPhoto));
            setUploadedImage(savedPhoto);
            // Reset file input when photo is loaded from saved data
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            if (imageRef.current) {
                imageRef.current.value = '';
            }
        }
    }, []);
    const handleTermsAndConditions = async (signature: string) => {
        setAcceptButtonLoading(true);
        setTermsAndConditions?.(true);
        let payload = {
            signature_photo: signature,
        };

        let res = await PatchAPI(
            `${type?.toLowerCase()?.split(" ").join("-")}/${user_id}`,
            payload
        );
        if (!res.error) {
            setTermsAndConditions?.(false)
            navigate(`/user-management/${type
                ?.toLowerCase()
                .replace(/ /g, "-")
                .slice(0, -1)}-management`);
            dispatch(showToast({ message: "Details added successfully", type: "success" }));
            dispatch(clearSignaturePath());
        } else {
            dispatch(showToast({ message: res.data.data.message, type: "error" }));
        }
        setAcceptButtonLoading(false);
    };

    const startCamera = async (): Promise<void> => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user" }
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (err) {
            setCaptureError("Unable to access camera. Please check permissions.");
            setShowCamera(false);
        }
    };
    const resetImages = (type: 'captured' | 'uploaded') => {
        if (type === 'captured') {
            setUploadedImage(null);
        } else {
            setImage(null);
            setIsCaptured(false);
        }
    };

    const stopCamera = (): void => {
        if (stream) {
            stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            setStream(null);
            setShowCamera(false);
        }
    };
    // Commented for now, might be used later
    // const capturePhoto = (): void => {
    //     if (videoRef.current && canvasRef.current) {
    //         const video = videoRef.current;
    //         const canvas = canvasRef.current;
    //         const context = canvas.getContext("2d");

    //         if (!context) return;

    //         canvas.width = video.videoWidth;
    //         canvas.height = video.videoHeight;

    //         // Scale and flip the context horizontally before drawing
    //         context.scale(-1, 1);
    //         context.translate(-canvas.width, 0);
    //         context.drawImage(video, 0, 0);

    //         // Reset the transformation
    //         context.setTransform(1, 0, 0, 1, 0, 0);

    //         const imageSrc = canvas.toDataURL("image/jpeg");
    //         setCapturedImage(imageSrc);
    //         setIsCaptured(true);
    //     }
    // };

    // const retakePhoto = (): void => {
    //     // startCamera();
    //     setCapturedImage(null);
    //     setIsCaptured(false);
    // };

    // const submitPhoto = (): void => {
    //     setImage(capturedImage);
    //     setIsModalOpen(false);
    //     setShowCamera(false);
    //     resetImages('captured');
    //     setError(null);
    //     setCaptureError(null);
    //     stopCamera();
    // };

    const validateFile = (file: File): boolean => {
        const validTypes = ["image/png", "image/jpg", "image/jpeg"];
        const maxSize = 10 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
            setError("Invalid file format.");
            return false;
        }

        if (file.size > maxSize) {
            setError("Upload Failed: File exceeds 10MB.");
            return false;
        }

        return true;
    };

    const handleFileUpload = (file: File): void => {
        setUploadedImage(null);
        if (validateFile(file)) {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const result = reader.result as string;
                setUploadedImage(result);
                handleFileSubmit('test')
                resetImages('uploaded');
                setError(null);
                setCaptureError(null);
                // Reset file inputs after successful upload
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                if (imageRef.current) {
                    imageRef.current.value = '';
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileUpload(file);
        }
    };

    const base64ToFile = async (base64String: string, fileName: string): Promise<File> => {
        const response = await fetch(base64String);
        const blob = await response.blob();
        return new File([blob], fileName, { type: blob.type });
    };
    useEffect(() => {
        if ((user_id || id)) {
            if (savedData?.photo) {
                setUploadedImage(config.cdnURL + 'public/' + savedData.photo);
            } else {
                setUploadedImage('')
            }
        }
    }, [])

    const handleFileSubmit = async (test?: string) => {
        let payload = {
            photo: ''
        };
        try {
            let file: File; // Declare a variable to hold the file
            const uploadPath = `uploads/${getUrlPath(type)}/photo`; // Path where the file will be uploaded

            // Check if a captured image exists
            if (capturedImage) {
                dispatch(setAddLoading(true)); // Dispatch loading state
                // Convert captured image (base64) to File format
                file = await base64ToFile(capturedImage, `selfie-${Date.now()}.jpg`);
            }
            // Check if an uploaded image exists
            else if (uploadedImage) {
                if (!test) {
                    dispatch(setAddLoading(true));
                } // Dispatch loading state
                if (uploadedImage.startsWith('data:image')) {
                    // Convert uploaded image (base64) to File format
                    file = await base64ToFile(uploadedImage, `photo-${Date.now()}.jpg`);

                    // Upload the image to S3 and get the key
                    const uploadedKey = await handleUpload(file!, uploadPath);

                    // Check if upload was successful
                    if (!uploadedKey) {
                        throw new Error("Failed to upload file"); // Throw error if upload failed
                    }

                    // Update payload with the correct file path
                    payload.photo = uploadedKey;

                } else {
                    // Extract just the path that starts with "uploads/" regardless of CDN URL
                    const uploadsPathMatch = uploadedImage.match(/(?:\/|^)(uploads\/.*?)$/);
                    if (uploadsPathMatch && uploadsPathMatch[1]) {
                        // Found a path that starts with "uploads/" - use just that part
                        payload.photo = uploadsPathMatch[1];
                    } else {
                        // If we can't extract the uploads path, just use the uploaded image as is
                        // This handles cases where it might already be properly formatted
                        payload.photo = uploadedImage;
                    }
                }
            } else if (!test) {
                // Photo is now optional for all users
                dispatch(setAddLoading(true));
                payload.photo = '';
            }
            if (test) {
                dispatch(setPhotoUpload(payload.photo));
                return;
            }
            // Call the PatchAPI to update the master distributor data with the uploaded photo
            const response = await PatchAPI(`${getUrlPath(type)}/${user_id}`, payload);

            // Check if the response doesn't contain an error
            if (!response.error) {
                // Dispatch success state and save the uploaded photo
                dispatch(setSavedData({ key: 'photo', value: payload.photo }));

                if (
                    type !== "Customers" ||
                    (type === "Customers" && selectedOption === "Corporate")
                ) {
                    handleNext(); // Proceed to the next step
                } else {
                    if (profile === 'Pending' || profile === '') {
                        setTermsAndConditions?.(true);
                    } else {
                        navigate(`/user-management/${type
                            ?.toLowerCase()
                            .replace(/ /g, "-")
                            .slice(0, -1)}-management`);
                    }
                }
            } else {
                // Set error message if response contains an error
                setError(response?.data?.response?.data?.message || 'Something went wrong');
                setCaptureError(response?.data?.response?.data?.message || 'Something went wrong');
            }

            // Stop loading state after operation completes
            dispatch(setAddLoading(false));

        } catch (err) {
            // Handle any unexpected errors
            console.error("Upload error:", err);
            setCaptureError("Failed to upload photo. Please try again.");
            setError('Failed to upload photo. Please try again.');
        }
    }
    const handleDelete = () => {
        setUploadedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (imageRef.current) {
            imageRef.current.value = '';
        }
        setError(null);
        setCaptureError(null);
        // Update Redux state to clear the photo
        dispatch(setPhotoUpload({ photo: '' }));
        dispatch(setSavedData({ key: 'photo', value: '' }));
    };

    const handleFileUploads = (file: File): void => {
        setUploadedImage(null);
        if (validateFile(file)) {
            const fileUrl = URL.createObjectURL(file); // Get file path as an object URL
            setUploadedImage(fileUrl);
            resetImages('uploaded');
            setError(null);
            setCaptureError(null);
        }
    };

    return (
        <div className="w-full">
            <div className="">
                {/* Commented for now, will be used later  */}
                {/* <div className="space-y-8 flex gap-10 w-full">
                    <div className={`${image ? '' : captureError ? 'border-2 border-dashed border-negative bg-white' : 'border-2 border-dashed border-gray-300'} rounded-lg p-4 h-64 relative w-1/3`}>
                        {image ? (
                            <div className="relative group h-full">
                                <img
                                    src={image}
                                    alt="Captured"
                                    className="w-full h-full object-cover rounded-lg"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => {
                                            setImage(null);
                                            setIsCaptured(false);
                                            setCapturedImage(null);
                                        }}
                                        className="relative"
                                    >
                                        <Image src={`delete-photo`} className="group1" />
                                        <div className="absolute left-1/2 top-full mt-3 -translate-x-1/2 hidden group-hover:block">
                                            <div className="relative bg-slate-300 text-black text-xs px-3 py-1 rounded shadow-lg">
                                                Delete
                                                <div
                                                    className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 bg-slate-300"
                                                    style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
                                                ></div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => {
                                    setIsModalOpen(true);
                                    setShowCamera(true);
                                }}
                                className={` h-[44px] mx-auto mt-[88px] gap-2 w-[110px] flex items-center justify-center rounded-lg border border-p_primary text-p_primary text-body1 transition`}
                            >
                                <Camera size={24} />
                                Capture
                            </button>
                        )}
                    </div>
                    <div className="w-1/3 h-64">
                        <div className="w-[470px] mt-[-20px]">
                            <h3 className="text-lg font-semibold mb-4">Live Selfie</h3>
                            <ul className="space-y-2 ml-[20px]">
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-black flex-shrink-0"></span>
                                    <span className="text-[#1F1F1F] font-medium text-base">Ensure you center your face within the capture area.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-black flex-shrink-0"></span>
                                    <span className="text-[#1F1F1F] font-medium text-base">Ensure you use a high-end camera</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-black flex-shrink-0"></span>
                                    <span className="text-[#1F1F1F] font-medium text-base">Hold steady before you take the selfie to avoid capturing during motion.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-black flex-shrink-0"></span>
                                    <span className="text-[#1F1F1F] font-medium text-base">Check the selfie before uploading; if possible, retake it until you get the right shot.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-center w-full mb-7">
                    <div className="w-full border-t border-2 border-[#EEEEEE]"></div>
                    <span className="px-2 text-[#000000] text-xl font-semibold">Or</span>
                    <div className="w-full border-t border-2 border-[#EEEEEE]"></div>
                </div> */}
                <div className="flex flex-wrap justify-start gap-10">
                    <div
                        className={`border-2 border-dashed rounded-lg w-full sm:w-1/3 p-4 h-64 relative ${isDragging ? "border-blue-500 bg-blue-50" : error ? 'border-negative bg-white' : "border-gray-300"
                            }`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        {uploadedImage ? (
                            <>
                                <div className="relative group h-full hover:opacity-80">
                                    <input
                                        ref={imageRef}
                                        type="file"
                                        className="hidden"
                                        id="photo"
                                        accept="image/png,image/jpeg,image/jpg"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleFileUpload(file);
                                        }} />
                                    <img
                                        src={uploadedImage}
                                        data-testid={`uploaded-image`}
                                        alt="Uploaded"
                                        className="w-full h-full object-cover rounded-lg" />
                                    <div className="absolute inset-0 flex gap-20 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => {
                                                // Reset the file input value to allow selecting the same file again
                                                if (imageRef.current) {
                                                    imageRef.current.value = '';
                                                    imageRef.current.click();
                                                }
                                            }}
                                            className="relative"
                                        >
                                            <Image src={`reupload`} className="group1" testId="reupload" />
                                            <div className="absolute left-1/2 top-full mt-3 -translate-x-1/2 hidden group-hover:block">
                                                <div className="relative bg-black text-white text-xs px-3 py-1 rounded shadow-lg">
                                                    Reupload
                                                    <div
                                                        className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 bg-black"
                                                        style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                </div>
                                <div className="w-full mt-8 flex justify-end">
                                    <button
                                        onClick={handleDelete}
                                        className="relative group"
                                        data-testid="delete-button"
                                    >
                                        <Image src={`delete_blue`} />
                                        <div className="absolute left-1/2 top-full mt-3 -translate-x-1/2 hidden group-hover:block">
                                            <div className="relative bg-black text-white text-xs px-3 py-1 rounded shadow-lg">
                                                Delete
                                                <div
                                                    className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 bg-black"
                                                    style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
                                                ></div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    id="photo"
                                    className="hidden"
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleFileUpload(file);
                                    }}
                                />
                                <button
                                    data-testid="photo"
                                    onClick={() => fileInputRef.current?.click()}
                                    className={` h-[44px] gap-2 w-[110px] flex items-center justify-center rounded-lg border border-p_primary text-p_primary text-body1 transition`}
                                >
                                    <div className={`-rotate-90 `}>
                                        <Image src="log-out" />
                                    </div>
                                    Upload
                                </button>
                                <p className="text-[#A4A9AE] mt-2">Or</p>
                                <p className="text-[#1F1F1F]">Drag & Drop</p>
                                <p className="text-[#A4A9AE] text-sm mt-2">Max file size: 10 MB</p>
                                <p className="text-[#A4A9AE] text-sm text-center">Supported file types: jpg, png and jpeg</p>
                            </div>
                        )}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold mt-5 mb-4">Upload Photo</h3>
                        <ul className="space-y-2 ml-[20px]">
                            <li className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 mt-2 rounded-full bg-black flex-shrink-0"></span>
                                <span className="text-[#1F1F1F] font-medium text-base">Good resolution & passport size photo is preferred</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {
                // isModalOpen && (
                //     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                //         <div className="relative max-w-[1000px] w-full mx-4">
                //             {/* Close button positioned relative to modal */}
                //             <div className="absolute top-3 right-2 z-10">
                //                 <Image
                //                     src="close"
                //                     className="cursor-pointer"
                //                     onClick={() => setIsModalOpen(false)}
                //                 />
                //             </div>

                //             {/* Modal content */}
                //             <div ref={modalRef} className="bg-white py-16 px-16 rounded-lg w-full h-[660px] relative">
                //                 <div className="flex flex-col items-center justify-center text-xl w-full mb-4">
                //                     <h1 className="text-black font-semibold mb-1">Live Selfie</h1>
                //                     <p className="font-medium text-sm">{`Click ${capturedImage ? `'Retake' to recapture` : `'Capture' to capture`} the image.`}</p>
                //                 </div>

                //                 {isCaptured ? (
                //                     <div className="flex flex-col items-center">
                //                         <img
                //                             src={capturedImage || ""}
                //                             alt="Captured"
                //                             className="w-full max-w-[647.5px] h-[396.2px] object-cover rounded-lg"
                //                         />
                //                         <div className="flex justify-center gap-4 mt-4">
                //                             <button
                //                                 onClick={() => {
                //                                     startCamera();
                //                                     retakePhoto();
                //                                 }}
                //                                 className="bg-white border border-[#144EA2] font-semibold text-lg text-[#144EA2] px-[32px] py-[16px] rounded-lg"
                //                             >
                //                                 Retake
                //                             </button>
                //                             <button
                //                                 onClick={submitPhoto}
                //                                 className="bg-[#144EA2] text-white font-semibold text-lg px-[32px] py-[16px] rounded-lg"
                //                             >
                //                                 Submit
                //                             </button>
                //                         </div>
                //                     </div>
                //                 ) : (
                //                     <div className="flex flex-col items-center">
                //                         <video
                //                             ref={videoRef}
                //                             autoPlay
                //                             playsInline
                //                             style={{ transform: 'scaleX(-1)' }}
                //                             className="w-full max-w-[647.5px] h-[396.2px] object-cover rounded-lg"
                //                         />
                //                         <canvas ref={canvasRef} className="hidden" />
                //                         <div className="flex justify-center mt-4">
                //                             <button
                //                                 onClick={capturePhoto}
                //                                 className="bg-[#144EA2] w-[139px] h-[60px] text-white px-8 py-4 rounded-lg"
                //                             >
                //                                 Capture
                //                             </button>
                //                         </div>
                //                     </div>
                //                 )}
                //             </div>
                //         </div>
                //     </div>
                // )
            }

            {(error) && <ErrorMessage errorMessage={(error) || ''} className="text-negative " />}
            {termsAndConditions && (
                <CustomersTermsAndConditions
                    isOpen={termsAndConditions}
                    onClose={() => {
                        setTermsAndConditions?.(false);
                        dispatch(clearSignaturePath());
                    }}
                    secondaryButtonText="Decline"
                    primaryButtonText="Accept"
                    maxWidth="max-w-[461px]"
                    buttonWidth="w-[85px]"
                    buttonLoading={acceptButtonLoading}
                    // primaryButtonClassName
                    handleSubmitButtonClick={handleTermsAndConditions}
                />
            )}
        </div>
    );
});

export default PhotoUploadWithValidation;